import React, { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { Button } from '@common/bw-ui/ui/button';
import { Input } from '@common/bw-ui/ui/input';
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
} from '@common/bw-ui/ui/select';
import { FilterIcon, SearchIcon } from '@common/bw-ui/icons/index';
import { linkTo } from '@portal/env/utils/strings';
import debounce from 'lodash/debounce';
import type { BlogPost } from '@portal/types/nexudus/blog';
import type { BlogCategory } from '@portal/env/hooks/fetchBlogs';

type Props = {
  categories: BlogCategory[];
  posts: BlogPost[];
};

export const BlogPostPageHeader = ({
  categories: apiCategories,
  posts,
}: Props) => {
  const router = useRouter();

  const [searchValue, setSearchValue] = useState(router.query.query ?? '');

  // Per instance and cancelled on unmount, so a pending search can't navigate
  // after the user has already left the page (e.g. clicked into an article)
  const debouncedNavigate = useMemo(
    () => debounce((navigate: () => void) => navigate(), 750),
    [],
  );
  useEffect(() => () => debouncedNavigate.cancel(), [debouncedNavigate]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchValue(query);
    debouncedNavigate(() => {
      void router.push(linkTo(router, { ...router.query, query, page: 1 }));
    });
  };

  const handleCategorySelect = (categoryId: number | null) => {
    void router.push(linkTo(router, { page: 1, categoryId }));
  };

  const postCategories = posts.flatMap((p) => p.BlogCategories ?? []);

  // Accumulate every category seen across all API responses. When a filter is
  // active, the API returns only categories for matching posts, so without this
  // the dropdown loses options and the user cannot switch to a different filter.
  // State is updated during render (guarded) rather than via a ref, per React's
  // "storing information from previous renders" pattern.
  const [seenCategories, setSeenCategories] = useState<BlogCategory[]>([]);
  const newCategories = [...postCategories, ...apiCategories].filter(
    (c, i, all) =>
      all.findIndex((other) => other.Id === c.Id) === i &&
      !seenCategories.some((seen) => seen.Id === c.Id),
  );
  if (newCategories.length) {
    setSeenCategories([...seenCategories, ...newCategories]);
  }
  const categories = [...seenCategories, ...newCategories];

  const activeCategoryId = router.query.categoryId
    ? Number(router.query.categoryId)
    : null;

  const categoryLabel = activeCategoryId
    ? (categories.find((c) => c.Id === activeCategoryId)?.Title ?? 'Categories')
    : 'Categories';

  return (
    <div className="tw:flex tw:flex-col tw:gap-4 tw:mb-8">
      <h3 className="tw:font-medium">Articles</h3>

      <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
        <div className="tw:relative tw:w-full tw:md:flex-1">
          <SearchIcon
            className="tw:absolute tw:left-3 tw:top-1/2 tw:-translate-y-1/2 tw:size-4 tw:text-muted tw:pointer-events-none"
            fill="gray"
            stroke="currentColor"
            strokeWidth={2}
          />
          <Input
            type="text"
            placeholder="Search articles..."
            className="tw:w-full tw:font-normal tw:pl-9"
            value={searchValue}
            onChange={handleSearchChange}
          />
        </div>
        <Select
          value={activeCategoryId?.toString() ?? 'all'}
          onValueChange={(val) =>
            handleCategorySelect(val === 'all' ? null : Number(val))
          }
        >
          <SelectTrigger asChild>
            <Button variant="outline" className="tw:font-normal">
              <FilterIcon className="tw:w-5 tw:h-5" fill="black" />
              {categoryLabel}
            </Button>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            {categories.map((category) => (
              <SelectItem key={category.Id} value={category.Id.toString()}>
                {category.Title}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};
